import React from "react";

// Tailwind CSS required. Drop this into a Next.js route like app/privacy/page.tsx
// or pages/privacy.tsx (Next 12).
// Replace dates/addresses if they change.

export default function PrivacyPolicyPage() {
  const [lang, setLang] = React.useState<"en" | "ar">("en");

  const Section: React.FC<{ id: string; title: string; children: React.ReactNode }>
    = ({ id, title, children }) => (
      <section id={id} className="scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-3">{title}</h2>
        <div className="prose max-w-none dark:prose-invert">{children}</div>
      </section>
    );

  const NavLink: React.FC<{ href: string; label: string }>
    = ({ href, label }) => (
      <a href={href} className="text-sm px-3 py-1 rounded-full border hover:bg-gray-50 dark:hover:bg-gray-800 transition">
        {label}
      </a>
    );

  return (
    <main className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50">
      <div className="mx-auto max-w-4xl px-4 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Privacy Policy</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Last updated: October 24, 2025</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Language:</span>
            <div className="inline-flex rounded-xl border overflow-hidden">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-sm ${lang === "en" ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-transparent"}`}
                aria-pressed={lang === "en"}
              >EN</button>
              <button
                onClick={() => setLang("ar")}
                className={`px-3 py-1.5 text-sm ${lang === "ar" ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-transparent"}`}
                aria-pressed={lang === "ar"}
              >AR</button>
            </div>
          </div>
        </div>

        {/* Sticky TOC */}
        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-6">
          <aside className="md:sticky md:top-6 h-max border rounded-2xl p-3 md:p-4 bg-neutral-50 dark:bg-neutral-900/40">
            <nav className="flex flex-wrap md:flex-col gap-2">
              <NavLink href="#controller" label={lang === "en" ? "Controller & EU Rep" : "مراقب البيانات والممثل"} />
              <NavLink href="#data" label={lang === "en" ? "Data We Collect" : "البيانات التي نجمعها"} />
              <NavLink href="#lawful" label={lang === "en" ? "Purpose & Legal Basis" : "الغرض والأساس القانوني"} />
              <NavLink href="#sharing" label={lang === "en" ? "Sharing & Transfers" : "مشاركة البيانات ونقلها"} />
              <NavLink href="#retention" label={lang === "en" ? "Retention" : "الاحتفاظ"} />
              <NavLink href="#security" label={lang === "en" ? "Security" : "الأمان"} />
              <NavLink href="#rights" label={lang === "en" ? "Your Rights" : "حقوقك"} />
              <NavLink href="#children" label={lang === "en" ? "Children" : "القُصّر"} />
              <NavLink href="#deletion" label={lang === "en" ? "Deletion" : "الحذف"} />
              <NavLink href="#changes" label={lang === "en" ? "Changes" : "تعديل السياسة"} />
              <NavLink href="#contact" label={lang === "en" ? "Contact" : "التواصل"} />
            </nav>
          </aside>

          {/* Content */}
          <div className="flex flex-col gap-8">
            {lang === "en" ? (
              <div>
                <p className="text-base leading-7 mb-6">
                  This Privacy Policy explains how <strong>ROAA DATA IT W.L.L</strong> ("Company", "we", "us")
                  collects, uses, and protects your personal information when you use the <strong>Loyalty Stamper / Roaait</strong>
                  application ("Service"). We comply with the <strong>EU GDPR</strong>, <strong>Bahrain PDPL</strong>, and <strong>Saudi PDPL</strong>.
                </p>

                <Section id="controller" title="1. Data Controller and EU Representative">
                  <ul>
                    <li><strong>Data Controller:</strong> ROAA DATA IT W.L.L (Kingdom of Bahrain) — Email: <a href="mailto:info@roaait.com">info@roaait.com</a></li>
                    <li><strong>EU Representative (GDPR Art. 27):</strong> Muhammad Samad, Karhoksgatan 86, 55612 Jönköping, Sweden — Email: <a href="mailto:info@roaait.com">info@roaait.com</a></li>
                  </ul>
                </Section>

                <Section id="data" title="2. Types of Personal Data Collected">
                  <ul>
                    <li><strong>Identification:</strong> name, email, phone, store name, store address</li>
                    <li><strong>Authentication:</strong> hashed passwords (no plaintext storage)</li>
                    <li><strong>Payments:</strong> processed by Amazon SDKs; we do not store card numbers</li>
                    <li><strong>Device & usage:</strong> IP, browser/app diagnostics, analytics (Google Analytics)</li>
                    <li><strong>Camera/media:</strong> pictures or QR codes scanned for loyalty rewards</li>
                    <li><strong>Customer data:</strong> end-customer name and phone collected by merchants</li>
                  </ul>
                </Section>

                <Section id="lawful" title="3. Purpose and Legal Basis for Processing">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-collapse">
                      <thead>
                        <tr className="bg-neutral-100 dark:bg-neutral-900">
                          <th className="p-2 text-left border">Purpose</th>
                          <th className="p-2 text-left border">Legal Basis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-2 border">Register and manage merchant accounts; issue loyalty cards</td>
                          <td className="p-2 border">Contract performance (GDPR Art. 6(1)(b))</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">Process payments</td>
                          <td className="p-2 border">Contract performance; Legal obligation</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">Transactional notifications & app updates</td>
                          <td className="p-2 border">Legitimate interests (Art. 6(1)(f))</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">Marketing & push notifications (opt-in)</td>
                          <td className="p-2 border">Consent (Art. 6(1)(a))</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">Analytics & service improvement</td>
                          <td className="p-2 border">Legitimate interests</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">Security, fraud prevention, legal compliance</td>
                          <td className="p-2 border">Legal obligation / Legitimate interests</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Section>

                <Section id="sharing" title="4. Data Sharing and Transfers">
                  <ul>
                    <li>Payment processors (Amazon Payment SDKs)</li>
                    <li>Apple Wallet & Google Wallet APIs (for digital passes)</li>
                    <li>Google Analytics (usage analytics)</li>
                    <li>Cloud hosting in the <strong>Netherlands (EEA)</strong></li>
                  </ul>
                  <p className="mt-3">Transfers outside the EEA (if any) rely on adequacy decisions or Standard Contractual Clauses (SCCs). We do not sell personal data.</p>
                </Section>

                <Section id="retention" title="5. Data Retention">
                  <p>
                    We retain personal data until account deletion or a deletion request. Security logs may be kept up to 12 months, unless law requires longer.
                  </p>
                </Section>

                <Section id="security" title="6. Security Measures">
                  <ul>
                    <li>TLS (HTTPS) in transit</li>
                    <li>Hashed passwords; least-privilege access controls</li>
                    <li>Role-based rules and secure EU hosting</li>
                  </ul>
                </Section>

                <Section id="rights" title="7. Your Data Protection Rights">
                  <ul>
                    <li>Access, rectification, erasure</li>
                    <li>Restriction and objection (including marketing)</li>
                    <li>Portability and withdrawal of consent</li>
                  </ul>
                  <p className="mt-2">To exercise rights, contact <a href="mailto:info@roaait.com">info@roaait.com</a>. We respond within 30 days.</p>
                </Section>

                <Section id="children" title="8. Children’s Data">
                  <p>
                    The Service is not directed to children under 16 in the EU (13 elsewhere). Merchants collecting minors’ data must obtain parental consent. If we learn of data collected without consent, we will delete it.
                  </p>
                </Section>

                <Section id="deletion" title="9. Data Deletion">
                  <p>
                    Request deletion by emailing <a href="mailto:info@roaait.com">info@roaait.com</a>. We will erase personal data except where retention is legally required (e.g., transaction records).
                  </p>
                </Section>

                <Section id="changes" title="10. Changes to This Policy">
                  <p>
                    We may update this Policy. Changes take effect upon posting with an updated date.
                  </p>
                </Section>

                <Section id="contact" title="11. Contact">
                  <p>
                    ROAA DATA IT W.L.L (Bahrain) — Email: <a href="mailto:info@roaait.com">info@roaait.com</a><br/>
                    EU Representative: Muhammad Samad, Karhoksgatan 86, 55612 Jönköping, Sweden — Email: <a href="mailto:info@roaait.com">info@roaait.com</a>
                  </p>
                </Section>
              </div>
            ) : (
              <div dir="rtl" className="text-right">
                <p className="text-base leading-7 mb-6">
                  تشرح هذه السياسة كيفية قيام <strong>شركة رؤى لتقنية المعلومات ذ.م.م</strong> ("الشركة") بجمع واستخدام وحماية بياناتك الشخصية عند استخدامك لتطبيق <strong>Loyalty Stamper / Roaait</strong>. نحن نلتزم بـ <strong>اللائحة العامة لحماية البيانات (GDPR)</strong> وبـ <strong>قوانين حماية البيانات في البحرين والسعودية (PDPL)</strong>.
                </p>

                <Section id="controller" title="1. مراقب البيانات والممثل في الاتحاد الأوروبي">
                  <ul>
                    <li><strong>مراقب البيانات:</strong> شركة رؤى لتقنية المعلومات ذ.م.م (مملكة البحرين) — البريد: <a href="mailto:info@roaait.com">info@roaait.com</a></li>
                    <li><strong>الممثل في الاتحاد الأوروبي (المادة 27):</strong> محمد سمد، كارهوكسغاتان 86، 55612 يونشوبينغ، السويد — البريد: <a href="mailto:info@roaait.com">info@roaait.com</a></li>
                  </ul>
                </Section>

                <Section id="data" title="2. البيانات التي نجمعها">
                  <ul>
                    <li><strong>التعريف:</strong> الاسم، البريد، الهاتف، اسم المتجر، عنوان المتجر</li>
                    <li><strong>المصادقة:</strong> كلمات مرور مشفرة (لا يتم تخزينها بنص واضح)</li>
                    <li><strong>المدفوعات:</strong> تُعالج عبر Amazon SDKs؛ لا نخزن أرقام البطاقات</li>
                    <li><strong>الجهاز والاستخدام:</strong> عنوان IP، تشخيصات التطبيق/المتصفح، التحليلات (Google Analytics)</li>
                    <li><strong>الكاميرا/الوسائط:</strong> الصور أو رموز QR الخاصة ببطاقات الولاء</li>
                    <li><strong>بيانات العملاء:</strong> اسم ورقم هاتف العميل النهائي الذي يجمعه التاجر</li>
                  </ul>
                </Section>

                <Section id="lawful" title="3. الغرض والأساس القانوني للمعالجة">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-collapse">
                      <thead>
                        <tr className="bg-neutral-100 dark:bg-neutral-900">
                          <th className="p-2 text-right border">الغرض</th>
                          <th className="p-2 text-right border">الأساس القانوني</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-2 border">إنشاء حسابات التجار وإدارة بطاقات الولاء</td>
                          <td className="p-2 border">تنفيذ العقد</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">معالجة المدفوعات</td>
                          <td className="p-2 border">تنفيذ العقد / التزام قانوني</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">إشعارات تشغيلية وتحديثات التطبيق</td>
                          <td className="p-2 border">مصلحة مشروعة</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">التسويق والإشعارات (بعد الموافقة)</td>
                          <td className="p-2 border">الموافقة</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">التحليلات وتحسين الخدمة</td>
                          <td className="p-2 border">مصلحة مشروعة</td>
                        </tr>
                        <tr>
                          <td className="p-2 border">الأمان ومنع الاحتيال والامتثال</td>
                          <td className="p-2 border">التزام قانوني / مصلحة مشروعة</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Section>

                <Section id="sharing" title="4. مشاركة البيانات ونقلها">
                  <ul>
                    <li>معالجو الدفع (Amazon Payment SDKs)</li>
                    <li>واجهات Apple Wallet و Google Wallet</li>
                    <li>Google Analytics للتحليلات</li>
                    <li>استضافة سحابية في <strong>هولندا (المنطقة الاقتصادية الأوروبية)</strong></li>
                  </ul>
                  <p className="mt-3">أي نقل خارج المنطقة الاقتصادية الأوروبية يستند إلى قرارات الملاءمة أو البنود التعاقدية القياسية (SCCs). لا نقوم ببيع البيانات الشخصية.</p>
                </Section>

                <Section id="retention" title="5. الاحتفاظ بالبيانات">
                  <p>نحتفظ بالبيانات حتى حذف الحساب أو طلب الحذف. قد تُحفظ سجلات الأمان لمدة تصل إلى 12 شهرًا ما لم يطلب القانون مدة أطول.</p>
                </Section>

                <Section id="security" title="6. الأمان">
                  <ul>
                    <li>تشفير TLS (HTTPS)</li>
                    <li>كلمات مرور مشفرة؛ وصول بمبدأ أقل الصلاحيات</li>
                    <li>قواعد أدوار واستضافة آمنة داخل الاتحاد الأوروبي</li>
                  </ul>
                </Section>

                <Section id="rights" title="7. حقوقك">
                  <ul>
                    <li>الوصول والتصحيح والحذف</li>
                    <li>تقييد المعالجة والاعتراض (بما في ذلك التسويق)</li>
                    <li>قابلية نقل البيانات وسحب الموافقة</li>
                  </ul>
                  <p className="mt-2">لممارسة الحقوق: <a href="mailto:info@roaait.com">info@roaait.com</a>. سنرد خلال 30 يومًا.</p>
                </Section>

                <Section id="children" title="8. بيانات القُصّر">
                  <p>
                    الخدمة غير موجهة للأطفال دون 16 عامًا في الاتحاد الأوروبي (أو دون 13 عامًا في أماكن أخرى). على التاجر الحصول على موافقة ولي الأمر عند جمع بيانات القُصّر. سنحذف أي بيانات يتم جمعها دون موافقة.
                  </p>
                </Section>

                <Section id="deletion" title="9. الحذف">
                  <p>
                    لطلب حذف البيانات راسلنا على <a href="mailto:info@roaait.com">info@roaait.com</a>. سنحذف البيانات باستثناء ما يتطلب القانون الاحتفاظ به.
                  </p>
                </Section>

                <Section id="changes" title="10. تعديل السياسة">
                  <p>قد نقوم بتحديث هذه السياسة من وقت لآخر، ويصبح التعديل نافذًا عند نشره مع تحديث التاريخ.</p>
                </Section>

                <Section id="contact" title="11. التواصل">
                  <p>
                    شركة رؤى لتقنية المعلومات ذ.م.م (البحرين) — البريد: <a href="mailto:info@roaait.com">info@roaait.com</a><br/>
                    ممثل الاتحاد الأوروبي: محمد سمد، كارهوكسغاتان 86، 55612 يونشوبينغ، السويد — البريد: <a href="mailto:info@roaait.com">info@roaait.com</a>
                  </p>
                </Section>
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-10 text-xs text-neutral-500 dark:text-neutral-400">
          <p>
            This page is provided for transparency and does not constitute legal advice. For legal interpretation, please consult qualified counsel.
          </p>
        </div>
      </div>
    </main>
  );
}
